/**
 * <promo-grid-rotator> — slides its direct .flex-grid__item children on a timer.
 * Attributes: data-speed (seconds between slides), data-dots ("true" to render dots).
 */
class PromoGridRotator extends HTMLElement {
  connectedCallback() {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.start(), { once: true });
    } else {
      this.start();
    }
  }

  disconnectedCallback() {
    clearInterval(this.timer);
    this.timer = null;
  }

  start() {
    if (this.timer) return;

    this.slides = [...this.querySelectorAll(':scope > .flex-grid__item')];
    this.show(0, 1);
    if (this.slides.length < 2) return;

    this.bindControls();
    this.bindSwipe();
    this.play();
  }

  play() {
    clearInterval(this.timer);
    const speed = (parseFloat(this.dataset.speed) || 5) * 1000;
    this.timer = setInterval(() => this.next(), speed);
  }

  bindControls() {
    const frame = this.closest('.promo-rotator__frame');
    this.nav = frame?.querySelector('.promo-rotator__nav');
    if (!this.nav) return;

    this.nav.querySelector('[name="previous"]')?.addEventListener('click', () => {
      this.prev();
      this.play();
    });
    this.nav.querySelector('[name="next"]')?.addEventListener('click', () => {
      this.next();
      this.play();
    });

    this.dots = frame.nextElementSibling?.classList.contains('promo-rotator__dots')
      ? frame.nextElementSibling
      : null;
    if (!this.dots) return;
    [...this.dots.children].forEach((dot, i) => {
      dot.addEventListener('click', () => {
        this.show(i, i >= this.index ? 1 : -1);
        this.play();
      });
    });
    this.show(this.index, 1);
  }

  bindSwipe() {
    const root = this.closest('.promo-rotator__frame') || this;
    let startX = 0;
    let startY = 0;
    let pointerId = null;
    let swiped = false;

    root.querySelectorAll('a, img').forEach((el) => {
      el.draggable = false;
    });
    root.addEventListener('dragstart', (event) => event.preventDefault());

    root.addEventListener('pointerdown', (event) => {
      if (event.pointerType === 'mouse' && event.button !== 0) return;
      if (event.target.closest('.promo-rotator__btn, .promo-rotator__dot')) return;
      startX = event.clientX;
      startY = event.clientY;
      pointerId = event.pointerId;
      swiped = false;
    });

    root.addEventListener('pointermove', (event) => {
      if (pointerId !== event.pointerId) return;
      const dx = event.clientX - startX;
      const dy = event.clientY - startY;
      if (Math.abs(dx) < 8 && Math.abs(dy) < 8) return;
      if (!root.hasPointerCapture(event.pointerId)) root.setPointerCapture(event.pointerId);
    });

    const finish = (event) => {
      if (pointerId !== event.pointerId) return;
      const dx = event.clientX - startX;
      const dy = event.clientY - startY;
      pointerId = null;
      if (Math.abs(dx) < 40 || Math.abs(dx) < Math.abs(dy)) return;
      swiped = true;
      if (dx < 0) this.next();
      else this.prev();
      this.play();
    };

    root.addEventListener('pointerup', finish);
    root.addEventListener('pointercancel', () => { pointerId = null; });

    root.addEventListener('click', (event) => {
      if (!swiped) return;
      event.preventDefault();
      event.stopPropagation();
      swiped = false;
    }, true);
  }

  next() {
    this.show((this.index + 1) % this.slides.length, 1);
  }

  prev() {
    this.show((this.index - 1 + this.slides.length) % this.slides.length, -1);
  }

  show(index, dir = 1) {
    const from = this.index;
    const incoming = this.slides[index];
    const goingNext = dir >= 0;

    if (from != null && from !== index && incoming) {
      incoming.style.transition = 'none';
      incoming.style.transform = goingNext ? 'translateX(100%)' : 'translateX(-100%)';
      incoming.offsetWidth;
      incoming.style.removeProperty('transition');
      incoming.style.removeProperty('transform');
    }

    this.index = index;
    this.slides.forEach((slide, i) => {
      const isLeaving = from != null && from !== index && i === from;
      slide.classList.toggle('is-active', i === index);
      slide.classList.toggle('is-leaving', isLeaving && goingNext);
      slide.classList.toggle('is-leaving-prev', isLeaving && !goingNext);
      slide.inert = i !== index;
    });

    const leaving = from != null ? this.slides[from] : null;
    if (leaving && leaving !== incoming) {
      const done = () => {
        leaving.style.transition = 'none';
        leaving.classList.remove('is-leaving', 'is-leaving-prev');
        leaving.offsetWidth;
        leaving.style.removeProperty('transition');
      };
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        done();
      } else {
        leaving.addEventListener('transitionend', done, { once: true });
      }
    }

    if (!this.dots) return;
    [...this.dots.children].forEach((dot, i) => {
      dot.setAttribute('aria-current', i === index ? 'true' : 'false');
    });
  }
}

if (!customElements.get('promo-grid-rotator')) {
  customElements.define('promo-grid-rotator', PromoGridRotator);
}
